import { getDispatchers } from '@client/new/helpers/types-utils';
import { Actions, Async, toAsyncFetched } from '@client/new/helpers/types';
import { SalesType } from '@client/new/common/types';
import { PATHS, navigate } from '@client/routes';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';
import { useCallback, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { toCollectionLink } from '@client/new/pages/collections/collection-links';
import { getSubLinks } from '@client/new/pages/collections/collections.mappers';
import { getSalesPageUrl } from '@client/new/helpers/landingPages';

import { useProgramsModel } from '../model/programs.model';
import { ProgramItem } from '../model/programs.types';

import { DispatcherResult } from '../model/programs.types';

import {
  applyFilters,
  toCardRows,
  toPersistedIndexOrder,
  toSalesTypeFromConsumerTabFilter,
  toViewMode,
} from './programs.card-mappers';
import {
  LinksModalData,
  ModalType,
  ProgramsFilters,
  ProgramsVM,
  ProgramsViewActionMap,
} from './programs.viewModel.types';

type ProgramsVMAction = Actions<ProgramsViewActionMap>;

const REFRESH_FAILED_FALLBACK = 'Could not refresh programs';

/**
 * Surfaces a refetch failure that followed a successful mutation. We do
 * NOT keep the confirmation modal open here -- the mutation itself
 * succeeded; the stale-but-visible list is acceptable until the next
 * cache refresh.
 */
const toastIfRefreshFailed = (result: DispatcherResult): void => {
  if (!result.ok) return;
  if (!result.refreshFailed) return;
  toast.error(result.refreshFailed || REFRESH_FAILED_FALLBACK);
};

const DEFAULT_FILTERS: ProgramsFilters = {
  status: 'all',
  offers: 'all',
  consumerTab: 'all',
};

const CLOSED_MODAL: ModalType = { type: 'closed' };

const EMPTY_PROGRAMS: ReadonlyArray<ProgramItem> = [];

const toCompleteReorderIds = ({
  programs,
  filteredPrograms,
  reorderedVisibleIds,
  salesType,
}: {
  programs: ReadonlyArray<ProgramItem>;
  filteredPrograms: ReadonlyArray<ProgramItem>;
  reorderedVisibleIds: Array<number>;
  salesType: SalesType;
}): Array<number> | null => {
  const bucketPrograms = toPersistedIndexOrder(
    programs.filter((p) => p.salesType === salesType),
  );
  const bucketIds = bucketPrograms.map((p) => p.id);
  const visibleIds = toPersistedIndexOrder(
    filteredPrograms.filter((p) => p.salesType === salesType),
  ).map((p) => p.id);
  const visibleSet = new Set(visibleIds);

  if (reorderedVisibleIds.length !== visibleIds.length) return null;

  const seen = new Set<number>();
  for (const id of reorderedVisibleIds) {
    if (!visibleSet.has(id) || seen.has(id)) return null;
    seen.add(id);
  }

  let visibleIndex = 0;
  const nextIds = bucketIds.map((id) => {
    if (!visibleSet.has(id)) return id;
    const nextId = reorderedVisibleIds[visibleIndex];
    visibleIndex += 1;
    return nextId;
  });

  if (nextIds.every((id, index) => id === bucketIds[index])) return null;

  return nextIds;
};

const isConfirmModalForProgram = (
  modal: ModalType,
  type: Exclude<ModalType['type'], 'closed' | 'links'>,
  programId: number,
): boolean => modal.type === type && modal.programId === programId;

const isArchiveModalForProgram = (
  modal: ModalType,
  programId: number,
): boolean =>
  (modal.type === 'archive' || modal.type === 'archiveInDraftOffer') &&
  modal.programId === programId;

/**
 * ViewModel for /programs. Owns presentation state and toast/analytics
 * surfacing. Does not fetch. Does not render. See AGENTS.md for the MVVM
 * contract.
 */
export const useProgramsVM = ({
  influencerId,
}: {
  influencerId?: string;
}): Async<ProgramsVM> => {
  const model = useProgramsModel({ influencerId });
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState<ProgramsFilters>(DEFAULT_FILTERS);
  const [modal, setModal] = useState<ModalType>(CLOSED_MODAL);
  const confirmInFlightRef = useRef(new Set<string>());

  // Narrow view of the fetched model. `null` while the model is still
  // not-fetched/fetching/errored so the hooks below stay unconditional.
  const modelValue = model.type === 'fetched' ? model.value : null;

  const isDnDEnabled =
    toSalesTypeFromConsumerTabFilter(filters.consumerTab) !== null;

  const _dispatch = async (action: ProgramsVMAction) => {
    trackSessionEvent('ViewAction', action);
    if (!modelValue) return;

    switch (action.type) {
      case 'Search':
        setSearchText(action.payload);
        return;

      case 'SetFilter': {
        const { status, ...rest } = action.payload;
        const VALID_STATUSES: ReadonlyArray<ProgramsFilters['status']> = [
          'all',
          'draft',
          'ready',
          'published',
          'archived',
        ];
        const validatedStatus =
          status !== undefined && VALID_STATUSES.includes(status)
            ? status
            : undefined;
        const patch = {
          ...rest,
          ...(validatedStatus !== undefined ? { status: validatedStatus } : {}),
        };
        setFilters((prev) => ({ ...prev, ...patch }));
        return;
      }

      case 'CloseModal':
        setModal(CLOSED_MODAL);
        return;

      case 'EditProgram': {
        const editProgram = modelValue.programs.find(
          (p) => p.id === action.payload,
        );
        const basePath = PATHS.CATEGORY_EDIT(
          action.payload,
          modelValue.creator.id,
        );
        const url = editProgram
          ? `${basePath}?programDerivedState=${editProgram.state}`
          : basePath;
        navigate(url);
        return;
      }

      case 'DuplicateProgram': {
        const result = await modelValue.duplicateProgram(action.payload);
        if (!result.ok) {
          toast.error(
            'message' in result ? result.message : 'Duplicate failed',
          );
          return;
        }
        toastIfRefreshFailed(result);
        return;
      }

      case 'ArchiveProgram': {
        const program = modelValue.programs.find(
          (p) => p.id === action.payload.id,
        );
        if (program && isOnlyInDraftOtpOffers(program)) {
          const offerNames =
            program.offerState.type === 'otp'
              ? program.offerState.offers.map((o) => o.name)
              : [];
          setModal({
            type: 'archiveInDraftOffer',
            programId: action.payload.id,
            programName: action.payload.name,
            offerNames,
            inFlight: false,
          });
          return;
        }
        setModal({
          type: 'archive',
          programId: action.payload.id,
          programName: action.payload.name,
          inFlight: false,
        });
        return;
      }

      case 'ConfirmArchive': {
        const activeModal = modal;
        if (
          !(
            activeModal.type === 'archive' ||
            activeModal.type === 'archiveInDraftOffer'
          ) ||
          activeModal.programId !== action.payload
        )
          return;

        const confirmKey = `archive:${action.payload}`;
        if (confirmInFlightRef.current.has(confirmKey)) return;
        confirmInFlightRef.current.add(confirmKey);
        setModal((prev) =>
          (prev.type === 'archive' || prev.type === 'archiveInDraftOffer') &&
          prev.programId === action.payload
            ? { ...prev, inFlight: true }
            : prev,
        );
        const result = await modelValue.archiveProgram(action.payload);
        confirmInFlightRef.current.delete(confirmKey);
        if (!result.ok) {
          toast.error('message' in result ? result.message : 'Archive failed');
          setModal((prev) =>
            (prev.type === 'archive' || prev.type === 'archiveInDraftOffer') &&
            prev.programId === action.payload
              ? { ...prev, inFlight: false }
              : prev,
          );
          return;
        }
        setModal((prev) =>
          isArchiveModalForProgram(prev, action.payload) ? CLOSED_MODAL : prev,
        );
        toastIfRefreshFailed(result);
        return;
      }

      case 'DeleteProgram':
        setModal({
          type: 'delete',
          programId: action.payload.id,
          programName: action.payload.name,
          inFlight: false,
        });
        return;

      case 'ConfirmDelete': {
        if (modal.type !== 'delete' || modal.programId !== action.payload)
          return;

        const confirmKey = `delete:${action.payload}`;
        if (confirmInFlightRef.current.has(confirmKey)) return;
        confirmInFlightRef.current.add(confirmKey);
        setModal((prev) =>
          prev.type === 'delete' && prev.programId === action.payload
            ? { ...prev, inFlight: true }
            : prev,
        );
        const result = await modelValue.deleteProgram(action.payload);
        confirmInFlightRef.current.delete(confirmKey);
        if (!result.ok) {
          toast.error('message' in result ? result.message : 'Delete failed');
          setModal((prev) =>
            prev.type === 'delete' && prev.programId === action.payload
              ? { ...prev, inFlight: false }
              : prev,
          );
          return;
        }
        setModal((prev) =>
          isConfirmModalForProgram(prev, 'delete', action.payload)
            ? CLOSED_MODAL
            : prev,
        );
        toastIfRefreshFailed(result);
        return;
      }

      case 'OpenLinks': {
        const program = modelValue.programs.find(
          (p) => p.id === action.payload,
        );
        if (!program) return;
        setModal({
          type: 'links',
          data: toLinksModalData(program, modelValue.creator.customURL),
        });
        return;
      }

      case 'OpenSubLinks': {
        const result = getSubLinks(
          modelValue.creator.customURL || undefined,
          modelValue.creator.mode === 'new',
        );
        if (!result) return;
        // Channel-level links reuse the per-program `links` modal -- empty
        // `programName` suppresses the per-program heading inside the modal.
        setModal({
          type: 'links',
          data: { programName: '', links: result.links },
        });
        return;
      }

      case 'SellWithSubscription':
        setModal({
          type: 'sellWithSubscriptionConfirm',
          programId: action.payload.id,
          programName: action.payload.name,
          inFlight: false,
        });
        return;

      case 'ConfirmSellWithSubscription': {
        if (
          modal.type !== 'sellWithSubscriptionConfirm' ||
          modal.programId !== action.payload
        )
          return;

        const confirmKey = `sellWithSubscription:${action.payload}`;
        if (confirmInFlightRef.current.has(confirmKey)) return;
        confirmInFlightRef.current.add(confirmKey);
        setModal((prev) =>
          prev.type === 'sellWithSubscriptionConfirm' &&
          prev.programId === action.payload
            ? { ...prev, inFlight: true }
            : prev,
        );
        const result = await modelValue.sellAsSubscription(action.payload);
        confirmInFlightRef.current.delete(confirmKey);
        if (result.ok === false) {
          toast.error(result.message ?? 'Sell with subscription failed');
          const shouldCloseModal =
            result.code === 'COLLECTION_CONTENT_NOT_READY';
          setModal((prev) =>
            prev.type === 'sellWithSubscriptionConfirm' &&
            prev.programId === action.payload
              ? shouldCloseModal
                ? CLOSED_MODAL
                : { ...prev, inFlight: false }
              : prev,
          );
          return;
        }
        setModal((prev) =>
          isConfirmModalForProgram(
            prev,
            'sellWithSubscriptionConfirm',
            action.payload,
          )
            ? CLOSED_MODAL
            : prev,
        );
        toastIfRefreshFailed(result);
        return;
      }

      case 'RemoveFromSubscription':
        setModal({
          type: 'removeFromSubscriptionConfirm',
          programId: action.payload.id,
          programName: action.payload.name,
          inFlight: false,
        });
        return;

      case 'ConfirmRemoveFromSubscription': {
        if (
          modal.type !== 'removeFromSubscriptionConfirm' ||
          modal.programId !== action.payload
        )
          return;

        const confirmKey = `removeFromSubscription:${action.payload}`;
        if (confirmInFlightRef.current.has(confirmKey)) return;
        confirmInFlightRef.current.add(confirmKey);
        setModal((prev) =>
          prev.type === 'removeFromSubscriptionConfirm' &&
          prev.programId === action.payload
            ? { ...prev, inFlight: true }
            : prev,
        );
        const result = await modelValue.removeFromSubscription(action.payload);
        confirmInFlightRef.current.delete(confirmKey);
        if (!result.ok) {
          toast.error(
            'message' in result
              ? result.message
              : 'Remove from subscription failed',
          );
          setModal((prev) =>
            prev.type === 'removeFromSubscriptionConfirm' &&
            prev.programId === action.payload
              ? { ...prev, inFlight: false }
              : prev,
          );
          return;
        }
        setModal((prev) =>
          isConfirmModalForProgram(
            prev,
            'removeFromSubscriptionConfirm',
            action.payload,
          )
            ? CLOSED_MODAL
            : prev,
        );
        toastIfRefreshFailed(result);
        return;
      }

      case 'Reorder': {
        if (!isDnDEnabled) return;

        const salesType = toSalesTypeFromConsumerTabFilter(filters.consumerTab);
        if (!salesType) return;

        const ids = toCompleteReorderIds({
          programs,
          filteredPrograms,
          reorderedVisibleIds: action.payload,
          salesType,
        });
        if (!ids) return;

        const result = await modelValue.reorder({
          ids,
          salesType,
        });

        if (!result.ok) {
          toast.error('message' in result ? result.message : 'Reorder failed');
        }
        return;
      }

      case 'AddToOfferNavigate':
        navigate(PATHS.BUNDLES(modelValue.creator.id));
        return;

      case 'OpenChannelLandingPageBuilder':
        navigate(PATHS.LANDING_PAGE(modelValue.creator.id));
        return;
    }
  };

  // Keep a stable dispatch identity across renders so memoized cardRows
  // don't invalidate on unrelated state changes (modal, search, filters).
  // The ref is updated each render; the wrapper function reads the latest
  // `_dispatch` closure on invocation.
  const dispatchRef = useRef(_dispatch);
  dispatchRef.current = _dispatch;
  const stableDispatch = useCallback(
    (action: ProgramsVMAction) => dispatchRef.current(action),
    [],
  );

  const dispatchers = useMemo(
    () => getDispatchers<ProgramsViewActionMap>(stableDispatch),
    [stableDispatch],
  );

  // Memoize the filter + card-row pipeline so modal/search state changes
  // that don't touch the program list don't retrigger the per-row mapping.
  // The dispatcher object from `getDispatchers` is stable within a given
  // call site, so it is safe to include in the deps.
  const programs = modelValue?.programs ?? EMPTY_PROGRAMS;
  const filteredPrograms = useMemo(
    () => applyFilters(programs, filters, searchText),
    [programs, filters, searchText],
  );
  const cardRows = useMemo(
    () =>
      modelValue
        ? toCardRows(filteredPrograms, {
            dispatch: dispatchers.dispatch,
            creator: modelValue.creator,
          })
        : [],
    [modelValue, filteredPrograms, dispatchers.dispatch],
  );

  if (model.type !== 'fetched') return model;

  const viewMode = toViewMode(
    model.value.programs.length,
    filteredPrograms.length,
    () => cardRows,
  );

  return toAsyncFetched({
    state: {
      searchText,
      filters,
      modal,
      viewMode,
      isDnDEnabled,
      creator: model.value.creator,
      isCreatorLive: model.value.isCreatorLive,
    },
    ...dispatchers,
  });
};

/**
 * Trigger condition for the archive-in-draft-offer dialog variant (§4.3 of
 * the business-logic spec):
 *  - Program's derived state is NOT yet PUBLISHED / ARCHIVED.
 *  - Program is attached to at least one OTP offer and all those offers
 *    are still DRAFT. A mixed DRAFT+PUBLISHED OTP membership makes the
 *    program PUBLISHED-derived and routes to the standard archive flow.
 *
 * The mapper already strips ARCHIVED bundles from `offerState.offers`, so
 * "every offer has status DRAFT" is a complete check against the visible
 * bundle set.
 */
const isOnlyInDraftOtpOffers = (program: ProgramItem): boolean => {
  if (program.state !== 'draft' && program.state !== 'ready') return false;
  if (program.offerState.type !== 'otp') return false;
  if (program.offerState.offers.length === 0) return false;
  return program.offerState.offers.every((o) => o.status === 'DRAFT');
};

const toLinksModalData = (
  program: ProgramItem,
  creatorCustomUrl: string,
): LinksModalData => {
  const landingPageUrl = getSalesPageUrl({
    customURL: creatorCustomUrl,
    id: program.id,
    name: program.name,
  });

  const links = [
    toCollectionLink('programLandingPage', landingPageUrl),
    program.offerState.type === 'otp'
      ? toCollectionLink('checkoutPage', `${landingPageUrl}/checkout`)
      : undefined,
    toCollectionLink('programDeepLink', program.deeplinkUrl),
  ].filter(
    (
      link,
    ): link is {
      title: string;
      url: string;
      description: string;
    } => Boolean(link),
  );

  return {
    programName: program.name,
    links,
  };
};
